import { EMPTY_FEATURES, type Features } from '@/utilities/features';

const attribute = `
    label
    id
    value
`;
const brand = `
    name
    relativeUrl
`;

const availability = `
    available
    availableFuture
    stock
`;

const breadcrumb = `
    name
    relativeUrl
    url
`;

const promotion = `
    componentId
    ownerId
    zone
    properties
`;

const image = `
    position
    type
    url
`;

const video = `
    id
    type
    parameters {
        key
        value
    }
`;

const price = `
    currencyCode
    type
    value
`;

const productDisplayProperties = `
    attributesDisplayMode
    currentVariantSelected
    detailPageVariation
    showQuantity
`;

const signing = `
    key
    text
`;

const signings = `
    discount {
        ${signing}
    }
    merchandise {
        ${signing}
    }
`;

const categoryPathNode = `
    id
    name
`;

const relatedProducts = `
    codes
    id
`;

const similarItems = `
    name
    relativeUrl
`;

const accordionProductDescription = `
    text
`;

const accordionAttributeGroups = `
    groups {
        label
        key
        attributes {
            ${attribute}
        }
    }
`;

const accordionFit = `
    attributes {
        ${attribute}
    }
    sizeTableId
`;

const accordionWashingInstructions = `
   attributes {
        ${attribute}
    }
`;

const accordionIngredients = `
    text
`;

const accordionReturnOldProduct = `
    text
    link {
        href
        title
    }
`;

const variantProduct = `
    availability {
        ${availability}
    }
    code
    deliveryTime
    eligibleForSendFromStore
    lowestPriceFlag
    minOrderQuantity
    lowestPrice {
        value
    }
    usps {
        type
        text
    }
    color
    current
    selectionImage {
        ${image}
    }
    images {
        ${image}
    }
    overriddenPrices {
        ${price}
    }
    sellingPrice {
        ${price}
    }
    signings {
        ${signings}
    }
    selectionImage {
        url
    }
    relatedProducts {
        ${relatedProducts}
    }
    trackingMetadata
    size
    url
`;

const product = `
    attributeGroups {
        label
        key
        attributes {
            ${attribute}
        }
    }
    relatedProducts {
     ${relatedProducts}
    }
    brand {
        ${brand}
    }
    availableFromPrice {
        ${price}
    }
    categoryPath {
        ${categoryPathNode}
    }
    shipping {
        supplier
    }
    code
    colorCount
    currentVariantProduct {
        ${variantProduct}
    }
    defaultVariantCode
    description
    designer
    displayName
    displayProperties {
        ${productDisplayProperties}
    }
    fitMessage
    gift
    name
    similarItems {
       ${similarItems}
    }
    subBrand {
        ${brand}
    }
    videos {
        ${video}
    }
    trackingMetadata
    url
    variantProducts (groupBy: COLOR) {
        ${variantProduct}
    }
`;

const navigation = `
    promotions {
        ${promotion}
    }
`;

const metaInformation = `
    canonicalUrl
    description
    title
    alternativeLanguages {
        locale
        url
    }
`;

const merchandisingTile = `
    content
    image {
        src
        alt
        focusPoint {
            x
            y
        }
    }
    title
    trackingPath
`;

const sizeOptions = `
    label
    placeholder
    options {
        available
        selected
        size
        url
    }
`;

const colorOption = `
    available
    color
    image {
        ${image}
    }
    selected
    sizeOptions {
        ${sizeOptions}
    }
    sku
    url
`;

const pdpColorOptions = `
    displayType
    hasSizeOptions
    label
    placeholder
    options {
        ${colorOption}
    }
`;

const ribbon = `
    cta {
        href
        title
    }
    text
    variant
`;

const sizeTable = `
    id
`;

const usp = `
    text
    type
`;

const displayedPrice = `
    value
    label
    strikeThrough
`;

// Can be renamed to price when we finished the PLP and PDP api migration.
export const priceV2 = `
    label
    showPriceDiscounted
    value
    recommendedPrice: details {
        ... on RecommendedPrice {
            recommended {
                ${displayedPrice}
            }
        }
    }
    originalPrice: details {
        ... on OriginalPrice {
            original {
                ${displayedPrice}
            }
        }
    }
    previousLowestAndOriginalPrice: details {
        ... on PreviousLowestAndOriginalPrice {
            original {
                ${displayedPrice}
            }
            previousLowest {
                ${displayedPrice}
            }
        }
    }
    recommendedAndOriginalPrice: details {
        ... on RecommendedAndOriginalPrice {
            original {
                ${displayedPrice}
            }
            recommended {
                ${displayedPrice}
            }
        }
    }
`;

export const carouselProduct = `
    brand {
        ${brand}
    }
    categoryPath
    code
    color
    displayName
    name
    price {
        ${priceV2}
    }
    primaryImage {
        url
    }
    secondaryImage {
        url
    }
    selection {
        sku
    }
    signings {
        discount
        merchandise
    }
    subBrand {
        ${brand}
    }
    trackingMetadata
    url
`;

const accordionShopTheItems = `
    suggestedProducts {
        ${carouselProduct}
    }
`;

const accordion = `
    title
    expanded
    productDescription: content {
        ... on ProductDescription {
            ${accordionProductDescription}
        }
    }
    attributeGroups: content {
        ... on AttributeGroups {
            ${accordionAttributeGroups}
        }
    }
    fit: content {
        ... on Fit {
            ${accordionFit}
        }
    }
    ingredients: content {
        ... on Ingredients {
            ${accordionIngredients}
        }
    }
    returnOldProduct: content {
        ... on ReturnOldProduct {
            ${accordionReturnOldProduct}
        }
    }
    washingInstructions: content {
        ... on WashingInstructions {
            ${accordionWashingInstructions}
        }
    }
    shopTheItems: content {
        ... on ShopTheItems {
            ${accordionShopTheItems}
        }
    }
`;

const productDetailsPageProduct = `
    availability {
        purchasable
        onlineOnly
        stock
    }
    brand {
        ${brand}
    }
    subBrand {
        ${brand}
    }
    categoryPath
    code
    displayName
    name
    features
    fitMessage
    url
    images {
        ${image}
    }
    name
    price {
        ${priceV2}
    }
    relatedProducts {
        ${relatedProducts}
    }
    shipping {
        supplier
    }
    signings {
        discount
        merchandise
    }
    similarItems {
        ${similarItems}
    }
    trackingMetadata
    size
    color
    colorOptions: variants {
        ... on PdpColorOptions {
            ${pdpColorOptions}
        }
    }
    sizeOptions: variants {
        ... on SizeOptions {
            ${sizeOptions}
        }
    }
    price {
        ${priceV2}
    }
    signings {
        discount
        merchandise
    }
    availableForBasket
    selection {
        sku
        mode
    }
    quantitySelection {
        minimum
        maximum
        label
        placeholder
    }
    videos {
        ${video}
    }
`;

export const productDetailsQuery = `
    product {
        ${product}
    }
    navigation {
        ${navigation}
    }
    metaInformation {
        ${metaInformation}
    }
`;

type ProductDetailsQuery = {
  productCode: string;
  productVariantCode: string;
  color?: string;
  locale?: string;
  environment: string;
};

export const productDetailKeys = {
  base: ['product-details'] as const,

  details: ({ productCode, productVariantCode, color, environment, locale }: ProductDetailsQuery) =>
    [
      ...productDetailKeys.base,
      productCode,
      productVariantCode,
      color,
      environment,
      locale,
    ] as const,
};

type GetProductDetailsQuery = {
  productCode: string;
  productVariantCode: string;
  color?: string;
  locale: string;
  features?: Features;
};

export const getProductDetailsQuery = ({
  locale,
  productCode,
  productVariantCode,
  features = EMPTY_FEATURES,
  color,
}: GetProductDetailsQuery) =>
  `query GetProductDetails {
        productDetails(
            productCode: "${productCode}"
            productVariantCode: "${productVariantCode}"
            locale: "${locale}"
            features: ${features.toGraphQLInputLiteral()}
        ) {
            ${productDetailsQuery}
        }
        productDetailsPage(
            code: "${productCode}"
            sku: "${productVariantCode}"
            features: ${features.toGraphQLInputLiteral()}
            ${color ? `color: "${color}"` : ''}
        ) {
            accordions {
                ${accordion}
            }
            ribbon {
                ${ribbon}
            }
            breadcrumbs {
                ${breadcrumb}
            }
            product {
                ${productDetailsPageProduct}
            }
            merchandisingTile {
                ${merchandisingTile}
            }
            sizeTable {
                ${sizeTable}
            }
            usps {
                ${usp}
            }
            metaInformation {
                ${metaInformation}
            }
        }
    }
`;
